import { FactorSeverity, RiskFactorScores } from '../components/RiskAreasWidget'

export interface ResearchStudy {
  name: string
  dateRange: string
  logic: string
  impact: string
  link?: string
}

export interface RiskFactorDefinition {
  id: string
  name: string
  shortName: string
  defaultWeight: number
  study: ResearchStudy
}

export const RISK_FACTORS: Record<string, RiskFactorDefinition> = {
  visual_hazards: {
    id: 'visual_hazards',
    name: 'Visual Hazards',
    shortName: 'Visual',
    defaultWeight: 0.4,
    study: {
      name: 'Suffolk / Newmetrix Study',
      dateRange: '2017 – 2020',
      logic:
        'Proves that unsafe observations such as missing PPE or ladder misuse are the primary precursors to accidents.',
      impact:
        'AI predicts 1 in 5 accidents with 81% accuracy by monitoring visual compliance.',
    },
  },
  human_factors: {
    id: 'human_factors',
    name: 'Human Factors',
    shortName: 'Fatigue',
    defaultWeight: 0.25,
    study: {
      name: 'Procore Research',
      dateRange: '2022 – 2024',
      logic:
        'Identifies the safety redline for worker fatigue and digital engagement.',
      impact:
        'Recordable incidents double once a worker exceeds 50 hours per week or skips toolbox talks.',
    },
  },
  operational_friction: {
    id: 'operational_friction',
    name: 'Operational Friction',
    shortName: 'Stacking',
    defaultWeight: 0.2,
    study: {
      name: 'Shepherd Study',
      dateRange: '2016 – 2023',
      logic:
        'Quantifies the danger of Trade Stacking, which is having too many subcontractors in one footprint.',
      impact:
        'High subcontractor density in a single zone increases Struck-by risks by over 30%.',
    },
  },
  documentation_quality: {
    id: 'documentation_quality',
    name: 'Documentation Quality',
    shortName: 'PTP',
    defaultWeight: 0.15,
    study: {
      name: 'Dodge SmartMarket Report',
      dateRange: '2021 – 2023',
      logic: 'Measures the specificity and depth of Pre-Task Planning or PTP.',
      impact:
        'Generic or check-the-box safety plans are flagged as high-risk signals for site complacency.',
    },
  },
}

export const severityToNumeric = (severity: FactorSeverity): number => {
  switch (severity) {
    case 'high':
      return 95
    case 'medium':
      return 65
    case 'low':
      return 35
    case 'none':
      return 10
    default:
      return 0
  }
}

export const mapRiskFactorScores = (scores: RiskFactorScores) => {
  const getHighest = (s1: FactorSeverity, s2: FactorSeverity) => {
    const v1 = severityToNumeric(s1)
    const v2 = severityToNumeric(s2)
    return v1 > v2 ? s1 : s2
  }

  return {
    visual_hazards: getHighest(scores.weather, scores.inspections),
    human_factors: getHighest(scores.workers, scores.incidents),
    operational_friction: scores.documents,
    documentation_quality: getHighest(scores.ptpQuality, scores.ptpEngagement),
  }
}

export const calculateRiskScore = (
  scores: RiskFactorScores,
  weights: Record<string, number> = {
    visual_hazards: 0.4,
    human_factors: 0.25,
    operational_friction: 0.2,
    documentation_quality: 0.15,
  },
) => {
  const mapped = mapRiskFactorScores(scores)

  const visualScore =
    severityToNumeric(mapped.visual_hazards) * weights.visual_hazards
  const humanScore =
    severityToNumeric(mapped.human_factors) * weights.human_factors
  const frictionScore =
    severityToNumeric(mapped.operational_friction) *
    weights.operational_friction
  const docScore =
    severityToNumeric(mapped.documentation_quality) *
    weights.documentation_quality

  return {
    total: Math.round(visualScore + humanScore + frictionScore + docScore),
    breakdown: {
      visual_hazards: Math.round(visualScore),
      human_factors: Math.round(humanScore),
      operational_friction: Math.round(frictionScore),
      documentation_quality: Math.round(docScore),
    },
    raw: {
      visual_hazards: severityToNumeric(mapped.visual_hazards),
      human_factors: severityToNumeric(mapped.human_factors),
      operational_friction: severityToNumeric(mapped.operational_friction),
      documentation_quality: severityToNumeric(mapped.documentation_quality),
    },
  }
}
